import React, { useState } from 'react'
import { plainToInstance } from 'class-transformer'
import { DropResult } from 'react-beautiful-dnd'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'
import { DateTime } from 'luxon'

import { useAlert } from '@hooks/useAlert'
import { useClient } from '@hooks/useClient'
import { DAY, getErrorMessage } from '@libs/utils'
import { ConfirmModal } from '@components/modals/ConfirmModal'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { HighlightBookBannerType } from '@models/highlightBanner/HighlightBookBannerType'
import { HighlightGroupBannerFormType } from '@models/highlightBanner/HighlightGroupBannerFormType'
import { HighlightBookBannerFormType } from '@models/highlightBanner/HighlightBookBannerFormType'
import { HighlightBannerFormType } from '@models/highlightBanner/HighlightBannerFormType'
import { PublishedEnum } from '@interfaces/mainBanner/PublishedEnum'
import { DisplayTypeEnum } from '@interfaces/DisplayTypeEnum'
import { BannerEnum } from '@interfaces/BannerEnum'
import { Loading } from '@components/Loading'
import { queryClient } from '@client/init'
import { HighlightBannerType } from '@models/highlightBanner/HighlightBannerType'
import { HighlightBannerBookPageProps } from './interface'

export function withHighlightBannerBookPage(
  Component: React.FC<HighlightBannerBookPageProps>
) {
  function WithHighlightBannerBookPage() {
    const client = useClient()
    const alert = useAlert()
    const { id } = useParams()
    const navigate = useNavigate()
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [publishedType, setPublishedType] = useState<PublishedEnum>(
      PublishedEnum.CUSTOM
    )
    const confirmRemoveModal = useModal({ modal: 'confirm' })
    const storyIdsModal = useModal({ modal: 'storyIdsModal' })
    const queryKey = 'highlight-book-banner-list'

    const { data, isLoading, refetch } = useQuery(
      [queryKey, id],
      () =>
        client!.highlightBannerClient.getHighlightGroupBookBanner(Number(id)),
      {
        onSuccess: res => {
          setPublishedType(res.publishedType)
        },
        enabled: !!id,
      }
    )

    const { mutateAsync: updateGroupBookBanner } = useMutation(
      (form: HighlightBannerFormType) =>
        client!.highlightBannerClient.updateHighlightGroupBanner(form),
      {
        onSuccess: async () => {
          await refetch()
        },
        onError: error => {
          const message = getErrorMessage(error)
          alert.error(message)
        },
      }
    )

    const { mutateAsync: enableBookBanner } = useMutation(
      (form: HighlightBookBannerFormType) =>
        client!.highlightBannerClient.updateBookBanner(form),
      {
        onSuccess: async () => {
          await refetch()
        },
        onError: error => {
          const message = getErrorMessage(error)
          alert.error(message)
        },
      }
    )

    const { mutateAsync: displayBookBanner } = useMutation(
      (form: HighlightBookBannerFormType) =>
        client!.highlightBannerClient.updateBookBanner(form),
      {
        onSuccess: (_, variables: HighlightBookBannerFormType) => {
          queryClient.setQueryData<HighlightBannerType | undefined>(
            [queryKey, id],
            (oldData?: HighlightBannerType) => {
              if (oldData) {
                const index = oldData.bookBanner?.findIndex(
                  b => b.id === variables.id
                )
                // eslint-disable-next-line no-param-reassign
                oldData.bookBanner[index] = {
                  ...oldData.bookBanner[index],
                  isOnAndroid: variables.isOnAndroid,
                  isOnIos: variables.isOnIos,
                  isOnWeb: variables.isOnWeb,
                }
              }
              return oldData
            }
          )
        },
        onError: error => {
          const message = getErrorMessage(error)
          alert.error(message)
        },
      }
    )

    const { mutateAsync: reorderBookBanner } = useMutation(
      ({
        bookId,
        newIndex,
      }: {
        bookId: number
        oldIndex: number
        newIndex: number
      }) =>
        client!.highlightBannerClient.updateOrderBookBanner(
          bookId,
          newIndex + 1
        ),
      {
        onSuccess: async () => {
          await refetch()
        },
        onError: () => {
          alert.error('เกิดข้อผิดพลาด')
        },
      }
    )

    const { mutateAsync: handleDeleteBanner } = useMutation(
      (posterId: number) =>
        client!.highlightBannerClient.removeBookBanner(posterId),
      {
        onSuccess: async () => {
          await refetch()
          confirmRemoveModal.hide()
          alert.success('ลบข้อมูลสำเร็จ')
        },
        onError: error => {
          const message = getErrorMessage(error)
          alert.error(message)
        },
      }
    )

    const { mutateAsync: handleDeleteBanners } = useMutation(
      (form: { highlightGroupId: number; bookIds: number[] }) =>
        client!.highlightBannerClient.removeBookBanners(
          form.highlightGroupId,
          form.bookIds
        ),
      {
        onSuccess: async () => {
          await refetch()
          confirmRemoveModal.hide()
        },
        onError: error => {
          const message = getErrorMessage(error)
          alert.error(message)
        },
      }
    )

    const { mutateAsync: toggleIsEbook } = useMutation(
      (form: HighlightBookBannerFormType) =>
        client!.highlightBannerClient.updateBookBanner(form),
      {
        onSuccess: (_, variables: HighlightBookBannerFormType) => {
          queryClient.setQueryData<HighlightBannerType | undefined>(
            [queryKey, id],
            (oldData?: HighlightBannerType) => {
              if (oldData) {
                const index = oldData.bookBanner?.findIndex(
                  b => b.id === variables.id
                )
                // eslint-disable-next-line no-param-reassign
                oldData.bookBanner[index] = {
                  ...oldData.bookBanner[index],
                  isEbook: variables.isEbook,
                }
              }
              return oldData
            }
          )
        },
        onError: error => {
          const message = getErrorMessage(error)
          alert.error(message)
        },
      }
    )

    function handleSelectItemAll(): void {
      if (data?.bookBanner) {
        const itemIds: number[] = data.bookBanner.map(
          (item: HighlightBookBannerType) => item.id
        )
        const selectedItemIds = itemIds?.filter(selectId =>
          selectedIds.includes(selectId)
        )
        if (itemIds?.length === selectedItemIds?.length) {
          setSelectedIds(prev =>
            prev.filter(
              (selectId: number) => !selectedItemIds?.includes(selectId)
            )
          )
        } else if (selectedItemIds?.length) {
          setSelectedIds(prev => [
            ...prev,
            ...itemIds.filter(selectId => !selectedItemIds?.includes(selectId)),
          ])
        } else {
          setSelectedIds(prev => [...prev, ...itemIds])
        }
      }
    }

    async function handleDragItem(result: DropResult) {
      const { destination, source } = result
      if (!destination) return
      if (destination.index === source.index) return
      await reorderBookBanner({
        bookId: data?.bookBanner[source.index].id || 0,
        oldIndex: source.index,
        newIndex: destination.index,
      })
    }

    function handleSelectItem(selectId: number) {
      const index = selectedIds.findIndex((val: number) => val === selectId)
      if (index !== -1) {
        setSelectedIds((prev: number[]) => {
          const temp = [...prev]
          temp.splice(index, 1)
          return temp
        })
      } else {
        setSelectedIds((prev: number[]) => [...prev, selectId])
      }
    }

    async function handleEnable(value: HighlightBookBannerType) {
      await enableBookBanner({
        highlightGroupId: Number(id),
        id: value.id,
        isActive: !value.isActive,
        linkWeb: value.linkWeb,
        linkIos: value.linkIos,
        linkAndroid: value.linkAndroid,
        isOnWeb: value.isOnWeb,
        isOnIos: value.isOnIos,
        isOnAndroid: value.isOnAndroid,
        isEbook: value.isEbook,
      })
    }

    async function handlePublishBanner(value: boolean) {
      await Promise.all(
        selectedIds.map(posterId =>
          client!.highlightBannerClient.updateActiveBookBanner({
            id: posterId,
            highlightGroupId: Number(id),
            isActive: value,
          })
        )
      )
      await refetch()
    }

    async function deleteBannerList() {
      await handleDeleteBanners({
        highlightGroupId: Number(id),
        bookIds: selectedIds,
      })
    }

    function handleDeleteBannerList() {
      confirmRemoveModal.show({
        content: <ConfirmModal.Title>ยืนยันการลบข้อมูล</ConfirmModal.Title>,
        onConfirm: async () => {
          await deleteBannerList()
        },
        onClose: () => confirmRemoveModal.hide(),
      })
    }

    async function startDateChange(date: Date) {
      const dateTime = DateTime.fromJSDate(date).toUTC().toString()
      const now = new Date().valueOf()
      const start = DateTime.fromJSDate(date).toUTC().valueOf()
      const end = DateTime.fromJSDate(new Date(data?.publishedTo as string))
        .toUTC()
        .valueOf()

      if (data?.publishedTo && dateTime >= data.publishedTo) {
        await updateGroupBookBanner({
          id: Number(id),
          bannerType: BannerEnum.BOOK,
          publishedFrom: DateTime.fromJSDate(date).toUTC().toString(),
          publishedTo: DateTime.fromJSDate(date).toUTC().toString(),
          isActive: start <= now && end >= now,
        })
      } else {
        await updateGroupBookBanner({
          id: Number(id),
          bannerType: BannerEnum.BOOK,
          publishedFrom: DateTime.fromJSDate(date).toUTC().toString(),
          isActive: start <= now && end >= now,
        })
      }
    }

    async function endDateChange(date: Date) {
      const now = new Date().valueOf()
      const start = DateTime.fromJSDate(new Date(data?.publishedFrom as string))
        .toUTC()
        .valueOf()
      const end = DateTime.fromJSDate(date).toUTC().valueOf()

      await updateGroupBookBanner({
        id: Number(id),
        bannerType: BannerEnum.BOOK,
        publishedTo: DateTime.fromJSDate(date).toUTC().toString(),
        isActive: start <= now && end >= now,
      })
    }

    async function publishChange(value: PublishedEnum) {
      if (value === PublishedEnum.IMMEDIATELY) {
        const nextDate = new Date(new Date(Date.now() + DAY * 30))
        await updateGroupBookBanner({
          id: Number(id),
          isActive: true,
          bannerType: BannerEnum.BOOK,
          publishedFrom: DateTime.fromJSDate(new Date()).toUTC().toString(),
          publishedTo: DateTime.fromJSDate(nextDate).toUTC().toString(),
        })
      }
      setPublishedType(value)
    }

    async function displayChangeActive(
      value: HighlightBookBannerType,
      type: DisplayTypeEnum
    ) {
      await displayBookBanner({
        highlightGroupId: Number(id),
        id: value.id,
        isActive: value.isActive,
        linkWeb: value.linkWeb,
        linkIos: value.linkIos,
        linkAndroid: value.linkAndroid,
        isOnWeb: type === DisplayTypeEnum.WEB ? !value.isOnWeb : value.isOnWeb,
        isOnIos: type === DisplayTypeEnum.IOS ? !value.isOnIos : value.isOnIos,
        isOnAndroid:
          type === DisplayTypeEnum.ANDROID
            ? !value.isOnAndroid
            : value.isOnAndroid,
        isEbook: value.isEbook,
      })
    }

    async function handleToggleIsEbook(value: HighlightBookBannerType) {
      await toggleIsEbook({
        highlightGroupId: Number(id),
        id: value.id,
        isActive: value.isActive,
        linkWeb: value.linkWeb,
        linkIos: value.linkIos,
        linkAndroid: value.linkAndroid,
        isOnWeb: value.isOnWeb,
        isOnIos: value.isOnIos,
        isOnAndroid: value.isOnAndroid,
        isEbook: !value.isEbook,
      })
    }

    function handleOpenStoryIdsModal(mode: 'create' | 'edit') {
      storyIdsModal.show({
        bannerId: Number(id),
        mode,
        existingIds:
          data?.bookBanner?.map((b: HighlightBookBannerType) => b.book?.id) ||
          [],
        onSuccess: () => {
          refetch()
        },
      })
    }

    if (isLoading) return <Loading className='text-center' />

    const newProps = {
      id: Number(id),
      data,
      initialValues: plainToInstance(HighlightGroupBannerFormType, data),
      selectedIds,
      publishedType,
      navigate,
      handleDragItem,
      handleSelectItemAll,
      handleSelectItem,
      handleEnable,
      handleDeleteBanner,
      handleDeleteBannerList,
      handlePublishBanner,
      startDateChange,
      endDateChange,
      publishChange,
      displayChangeActive,
      handleToggleIsEbook,
      handleOpenStoryIdsModal,
    }

    return <Component {...newProps} />
  }

  return WithHighlightBannerBookPage
}
